import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowLeft, Loader2, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import BagsAfterHours from "../components/BagsAfterHours";

const MONTHS = ["Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie", "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];
const WEEKDAYS = ["Lu", "Ma", "Mi", "Jo", "Vi", "Sâ", "Du"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function Stepper({ label, value, onChange, testId }) {
  const num = parseFloat(value) || 0;
  return (
    <div className="mb-4">
      <div className="text-sm font-bold text-zinc-600 uppercase mb-1.5">{label}</div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onChange(String(Math.max(0, num - 0.5)))}
          className="w-14 h-14 rounded-xl bg-zinc-100 active:bg-zinc-200 text-2xl font-black text-zinc-700 flex-shrink-0"
          data-testid={`${testId}-minus`}
        >−</button>
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(",", "."))}
          placeholder="0"
          className="flex-1 h-14 text-center text-2xl font-black border-2 border-zinc-200 rounded-xl focus:border-emerald-500 outline-none min-w-0"
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          onClick={() => onChange(String(Math.min(16, num + 0.5)))}
          className="w-14 h-14 rounded-xl bg-emerald-100 active:bg-emerald-200 text-2xl font-black text-emerald-700 flex-shrink-0"
          data-testid={`${testId}-plus`}
        >+</button>
      </div>
    </div>
  );
}

export default function OreSimple() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const today = iso(new Date());
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [persons, setPersons] = useState([]);
  const [personId, setPersonId] = useState(user?.person_id || "");
  const [days, setDays] = useState({});
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null); // { date, hours, hoursDriven, siteKey }
  const [sites, setSites] = useState({ projects: [], regii: [] });
  const [saving, setSaving] = useState(false);
  const [bagsPrompt, setBagsPrompt] = useState(null);

  useEffect(() => {
    api.get("/simple-hours/persons").then((r) => {
      setPersons(r.data.persons || []);
      if (!personId && r.data.self_person_id) setPersonId(r.data.self_person_id);
    }).catch(() => toast.error("Nu am putut încărca lista de angajați"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMonth = useCallback(() => {
    if (!personId) return;
    setLoading(true);
    api.get("/simple-hours/month", { params: { person_id: personId, year: cursor.y, month: cursor.m + 1 } })
      .then((r) => setDays(r.data.days || {}))
      .catch(() => toast.error("Eroare la încărcarea orelor"))
      .finally(() => setLoading(false));
  }, [personId, cursor]);

  useEffect(() => { loadMonth(); }, [loadMonth]);

  const grid = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const lead = (first.getDay() + 6) % 7; // Luni = 0
    const dim = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= dim; d++) cells.push(iso(new Date(cursor.y, cursor.m, d)));
    return cells;
  }, [cursor]);

  const openDay = (dateStr) => {
    if (dateStr > today) return;
    const e = days[dateStr] || {};
    const siteKey = e.project_id ? `p:${e.project_id}` : e.regie_id ? `r:${e.regie_id}` : "";
    setModal({
      date: dateStr,
      hours: e.hours ? String(e.hours) : "",
      hoursDriven: e.hours_driven ? String(e.hours_driven) : "",
      siteKey,
      existing: !!(e.hours || e.hours_driven),
      label: e.label,
    });
    api.get("/simple-hours/sites", { params: { day: dateStr } }).then((r) => {
      const s = r.data || { projects: [], regii: [] };
      // Dacă șantierul existent nu e în listă (proiect finalizat), îl adăugăm pentru afișare
      if (e.project_id && !(s.projects || []).some((p) => p.id === e.project_id)) {
        s.projects = [{ id: e.project_id, name: e.label || "Șantier existent" }, ...(s.projects || [])];
      }
      if (e.regie_id && !(s.regii || []).some((rg) => rg.id === e.regie_id)) {
        s.regii = [{ id: e.regie_id, name: e.label || "Regie existentă" }, ...(s.regii || [])];
      }
      setSites(s);
    }).catch(() => setSites({ projects: [], regii: [] }));
  };

  const save = async (asDelete = false) => {
    if (!modal) return;
    const h = asDelete ? 0 : parseFloat(modal.hours) || 0;
    const hd = asDelete ? 0 : parseFloat(modal.hoursDriven) || 0;
    const [kind, sid] = (modal.siteKey || "").split(":");
    if (!asDelete && h + hd > 0 && !sid) return toast.error("Alege șantierul!");
    setSaving(true);
    try {
      await api.post("/simple-hours/set", {
        person_id: personId,
        date: modal.date,
        hours: h,
        hours_driven: hd,
        project_id: kind === "p" ? sid : null,
        regie_id: kind === "r" ? sid : null,
      });
      toast.success(asDelete || h + hd <= 0 ? "Orele au fost șterse" : `Salvat: ${h}h${hd > 0 ? ` + ${hd}h conduse` : ""}`);
      const savedDate = modal.date;
      const isSelf = personId === user?.person_id;
      const projId = kind === "p" ? sid : null;
      setModal(null);
      loadMonth();
      if (!asDelete && isSelf && projId && h + hd > 0) {
        try {
          const r = await api.get("/me/bags-prompt", { params: { project_id: projId, date: savedDate } });
          if (r.data?.ask) {
            const pname = (sites.projects || []).find((p) => p.id === projId)?.name || "";
            setBagsPrompt({ projectId: projId, projectName: pname });
          }
        } catch { /* ignore */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const prevMonth = () => setCursor((c) => (c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }));
  const nextMonth = () => setCursor((c) => (c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }));

  const totals = useMemo(() => {
    let h = 0, hd = 0;
    Object.values(days).forEach((e) => { h += e.hours || 0; hd += e.hours_driven || 0; });
    return { h, hd };
  }, [days]);

  const selPerson = persons.find((p) => p.id === personId);

  return (
    <div className="min-h-screen bg-zinc-50 pb-10" data-testid="ore-simple-page">
      <div className="max-w-md mx-auto px-3 pt-3">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => navigate("/")} className="w-11 h-11 rounded-xl bg-white shadow-sm flex items-center justify-center active:bg-zinc-100" data-testid="ore-simple-back-btn">
            <ArrowLeft className="w-5 h-5 text-zinc-700" />
          </button>
          <h1 className="text-xl font-black text-zinc-900 flex-1">Calendar Ore</h1>
          {loading && <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />}
        </div>

        {/* Selector angajat */}
        <select
          value={personId}
          onChange={(e) => setPersonId(e.target.value)}
          className="w-full h-12 mb-3 px-3 rounded-xl border-2 border-zinc-200 bg-white text-base font-bold text-zinc-800 outline-none focus:border-emerald-500"
          data-testid="ore-simple-person-select"
        >
          {!personId && <option value="">Alege angajatul…</option>}
          {persons.map((p) => (
            <option key={p.id} value={p.id}>{p.id === user?.person_id ? `${p.name} (eu)` : p.name}</option>
          ))}
        </select>

        {/* Navigare lună */}
        <div className="flex items-center justify-between bg-white rounded-xl shadow-sm px-2 py-2 mb-3">
          <button onClick={prevMonth} className="w-11 h-11 rounded-lg active:bg-zinc-100 flex items-center justify-center" data-testid="ore-simple-prev-month">
            <ChevronLeft className="w-6 h-6 text-zinc-700" />
          </button>
          <div className="text-lg font-black text-zinc-900" data-testid="ore-simple-month-label">{MONTHS[cursor.m]} {cursor.y}</div>
          <button onClick={nextMonth} className="w-11 h-11 rounded-lg active:bg-zinc-100 flex items-center justify-center" data-testid="ore-simple-next-month">
            <ChevronRight className="w-6 h-6 text-zinc-700" />
          </button>
        </div>

        {/* Calendar */}
        <div className="bg-white rounded-2xl shadow-sm p-2">
          <div className="grid grid-cols-7 mb-1">
            {WEEKDAYS.map((w, i) => (
              <div key={w} className={`text-center text-xs font-black uppercase py-1 ${i >= 5 ? "text-red-400" : "text-zinc-400"}`}>{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {grid.map((dateStr, i) => {
              if (!dateStr) return <div key={`x${i}`} />;
              const e = days[dateStr];
              const has = e && (e.hours > 0 || e.hours_driven > 0);
              const isToday = dateStr === today;
              const isFuture = dateStr > today;
              const dayNum = parseInt(dateStr.slice(8), 10);
              return (
                <button
                  key={dateStr}
                  onClick={() => openDay(dateStr)}
                  disabled={isFuture}
                  className={`h-16 rounded-xl flex flex-col items-center justify-center transition-colors ${
                    has ? "bg-emerald-500 text-white active:bg-emerald-600"
                      : isFuture ? "bg-zinc-50 text-zinc-300"
                      : "bg-zinc-100 text-zinc-700 active:bg-zinc-200"
                  } ${isToday ? "ring-2 ring-blue-500 ring-offset-1" : ""}`}
                  data-testid={`ore-simple-day-${dateStr}`}
                >
                  <span className={`text-xs font-bold ${has ? "text-emerald-100" : ""}`}>{dayNum}</span>
                  {has ? (
                    <span className="text-base font-black leading-tight">{(e.hours + e.hours_driven) % 1 === 0 ? e.hours + e.hours_driven : (e.hours + e.hours_driven).toFixed(1)}h</span>
                  ) : (
                    !isFuture && <span className="text-lg font-light text-zinc-300">+</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Total lunar */}
        <div className="mt-3 bg-white rounded-xl shadow-sm px-4 py-3 flex items-center justify-between" data-testid="ore-simple-totals">
          <div className="text-sm font-bold text-zinc-500 uppercase">Total {MONTHS[cursor.m]}</div>
          <div className="text-lg font-black text-zinc-900">
            {totals.h % 1 === 0 ? totals.h : totals.h.toFixed(1)}h
            {totals.hd > 0 && <span className="text-amber-600 ml-2">+ {totals.hd % 1 === 0 ? totals.hd : totals.hd.toFixed(1)}h conduse</span>}
          </div>
        </div>
      </div>

      {/* Modal editare zi */}
      {modal && (
        <div className="fixed inset-0 z-[150] bg-black/50 flex items-end sm:items-center justify-center" onClick={() => !saving && setModal(null)}>
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl p-5 pb-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ore-simple-day-modal">
            <div className="w-12 h-1.5 bg-zinc-200 rounded-full mx-auto mb-4 sm:hidden" />
            <div className="text-lg font-black text-zinc-900 mb-1">
              {new Date(modal.date + "T12:00:00").toLocaleDateString("ro-RO", { weekday: "long", day: "numeric", month: "long" })}
            </div>
            <div className="text-sm text-zinc-500 mb-4">{selPerson?.name || ""}</div>

            <div className="mb-4">
              <div className="text-sm font-bold text-zinc-600 uppercase mb-1.5">Șantier</div>
              <select
                value={modal.siteKey}
                onChange={(e) => setModal((m) => ({ ...m, siteKey: e.target.value }))}
                className="w-full h-14 px-3 rounded-xl border-2 border-zinc-200 bg-white text-base font-bold text-zinc-800 outline-none focus:border-emerald-500"
                data-testid="ore-simple-site-select"
              >
                <option value="">Alege șantierul…</option>
                {sites.projects?.length > 0 && (
                  <optgroup label="Proiecte">
                    {sites.projects.map((p) => <option key={p.id} value={`p:${p.id}`}>{p.name}</option>)}
                  </optgroup>
                )}
                {sites.regii?.length > 0 && (
                  <optgroup label="Regii">
                    {sites.regii.map((r) => <option key={r.id} value={`r:${r.id}`}>{r.name}</option>)}
                  </optgroup>
                )}
              </select>
            </div>

            <Stepper label="Ore lucrate" value={modal.hours} onChange={(v) => setModal((m) => ({ ...m, hours: v }))} testId="ore-simple-hours" />
            <Stepper label="Ore conduse" value={modal.hoursDriven} onChange={(v) => setModal((m) => ({ ...m, hoursDriven: v }))} testId="ore-simple-driven" />

            <button
              onClick={() => save(false)}
              disabled={saving}
              className="w-full h-14 rounded-xl bg-emerald-600 active:bg-emerald-700 text-white text-lg font-black disabled:opacity-50 mb-2"
              data-testid="ore-simple-save-btn"
            >
              {saving ? "Se salvează…" : "SALVEAZĂ"}
            </button>
            {modal.existing && (
              <button
                onClick={() => save(true)}
                disabled={saving}
                className="w-full h-12 rounded-xl border-2 border-red-200 text-red-600 font-bold flex items-center justify-center gap-2 active:bg-red-50"
                data-testid="ore-simple-delete-btn"
              >
                <Trash2 className="w-4 h-4" /> Șterge orele din această zi
              </button>
            )}
          </div>
        </div>
      )}

      {bagsPrompt && (
        <BagsAfterHours projectId={bagsPrompt.projectId} projectName={bagsPrompt.projectName} onDone={() => setBagsPrompt(null)} />
      )}
    </div>
  );
}
